import { createContext, useCallback, useContext } from 'react';

import { useSurface, type Surface } from './surface.context';

export type ConfirmVariant = 'default' | 'destructive' | 'warning';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** Highlighted warning shown under the description (e.g. "it is published"). */
  notice?: string;
  /**
   * When set, the confirm button stays disabled until the user types this
   * exact text (surrounding spaces ignored). For deletions that lose content.
   */
  requireText?: string;
}

export interface ConfirmContextValue {
  /** `surface` is the palette of the caller, so the dialog matches the tool it was opened from. */
  confirm: (opts: ConfirmOptions, surface?: Surface) => Promise<boolean>;
}

export const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  // Captured here, at the call site: the dialog itself is mounted at the app
  // root (outside the editor's `dark` subtree), so only the caller knows
  // which palette it is in.
  const surface = useSurface();
  const bound = useCallback(
    (opts: ConfirmOptions) => (ctx ? ctx.confirm(opts, surface) : Promise.resolve(false)),
    [ctx, surface],
  );
  if (!ctx) {
    throw new Error(
      'useConfirm() must be called inside <ConfirmDialogProvider>. ' +
        'Wrap the app root with it (see apps/urbania360/app/src/main.tsx).',
    );
  }
  return bound;
}
